import { useCallback, useRef } from 'react';
import {
  buildFolderTruncationMessage,
  FOLDER_UPLOAD_MESSAGES,
  isFolderPickerSupported,
  pickOneFolderImages,
  resolveFolderOrFilesDrop,
  takeOneFolderImagesFromFileList,
} from '@/utils/folderImagePicker';
import { IPE_UPLOAD_ACCEPT } from '@/utils/ipeImageUploadFormats';

/**
 * Studio 功能页统一：选择单个文件夹 + 拖拽（单文件夹 / 散文件）。
 *
 * @param {object} options
 * @param {(files: File[]|FileList) => void|Promise<void>} options.onFiles
 * @param {(message: string, type?: string) => void} [options.toast]
 * @param {boolean} [options.disabled]
 * @param {boolean} [options.folderEnabled=true] 单图槽可关闭文件夹通道
 */
export default function useStudioFolderUpload({
  onFiles,
  toast,
  disabled = false,
  folderEnabled = true,
} = {}) {
  const folderInputRef = useRef(null);
  const onFilesRef = useRef(onFiles);
  const toastRef = useRef(toast);
  onFilesRef.current = onFiles;
  toastRef.current = toast;

  const deliverFolderFiles = useCallback((selection) => {
    if (!selection?.files?.length) {
      toastRef.current?.(FOLDER_UPLOAD_MESSAGES.empty, 'warning');
      return false;
    }
    if (selection.truncated) {
      toastRef.current?.(buildFolderTruncationMessage(selection.totalMatched), 'info');
    }
    void onFilesRef.current?.(selection.files);
    return true;
  }, []);

  const openFolderPicker = useCallback(async () => {
    if (disabled || !folderEnabled) return;
    if (!isFolderPickerSupported()) {
      folderInputRef.current?.click();
      return;
    }
    try {
      const result = await pickOneFolderImages();
      if (result.cancelled) return;
      deliverFolderFiles(result);
    } catch (error) {
      toastRef.current?.(error?.message || '选择文件夹失败', 'error');
    }
  }, [deliverFolderFiles, disabled, folderEnabled]);

  const handleFolderInputChange = useCallback((event) => {
    if (disabled || !folderEnabled) {
      if (event?.target) event.target.value = '';
      return;
    }
    const selection = takeOneFolderImagesFromFileList(event.target.files);
    event.target.value = '';
    deliverFolderFiles(selection);
  }, [deliverFolderFiles, disabled, folderEnabled]);

  /**
   * 处理 DataTransfer：多文件夹拒绝、单文件夹读一层、散文件走 onFiles。
   * 调用方负责 preventDefault / 清 drag 状态；disabled 时直接返回 false。
   * @returns {Promise<boolean>} 是否已处理
   */
  const ingestDataTransfer = useCallback(async (dataTransfer) => {
    if (disabled) return false;
    try {
      const resolved = await resolveFolderOrFilesDrop(dataTransfer);
      if (resolved.kind === 'error') {
        toastRef.current?.(
          resolved.message || FOLDER_UPLOAD_MESSAGES.multiFolder,
          'warning',
        );
        return true;
      }
      if (resolved.kind === 'folder') {
        if (!folderEnabled) {
          toastRef.current?.('当前入口不支持文件夹上传，请选择图片文件', 'warning');
          return true;
        }
        deliverFolderFiles(resolved);
        return true;
      }
      if (!resolved.files?.length) {
        toastRef.current?.('请拖拽图片文件', 'warning');
        return true;
      }
      void onFilesRef.current?.(resolved.files);
      return true;
    } catch (error) {
      toastRef.current?.(error?.message || '读取文件夹失败', 'error');
      return true;
    }
  }, [deliverFolderFiles, disabled, folderEnabled]);

  return {
    folderInputRef,
    openFolderPicker,
    handleFolderInputChange,
    ingestDataTransfer,
    folderEnabled,
    folderAccept: IPE_UPLOAD_ACCEPT,
  };
}

export { FOLDER_UPLOAD_MESSAGES };
